import { FC } from 'react';
import {
  displayScorecardElementName,
  ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL,
  isScorecardTermElement,
  ScorecardElementSymbol,
} from '../../../Scorecard';
import { useTranslation } from 'react-i18next';
import { displayRate, displayTerm, elementsTitleTextTailwindClasses } from '../../../util';
import { Icon } from '../Icon';
import clsx from 'clsx';
import BeachModeIllustration from '../../../assets/BeachModeIllustration.svg';
import { usePresentationViewOptionsContext } from '../..';
import { useEmojiCalculation } from './useEmojiCalculation';

type ElementTileTargetIconPercentageScoreProps = {
  percentage?: number | null;
};

const ElementTileTargetIconPercentageScore: FC<ElementTileTargetIconPercentageScoreProps> = ({ percentage }) => (
  <>
    <span className={'text-[21px] sm:text-[55px] font-extrabold'}>
      {displayRate(percentage, { withSymbol: false, zeroIsDash: false })}
    </span>
    <span className={'opacity-50 text-[21px] sm:text-[55px] font-bold'}>%</span>
  </>
);

type ElementTileTargetIconNumericScoreProps = {
  value?: number | null;
  fallbackDisplayValue?: string;
};

const ElementTileTargetIconNumericScore: FC<ElementTileTargetIconNumericScoreProps> = ({
  value,
  fallbackDisplayValue = '--',
}) => {
  if (value === null || value === undefined) {
    return <div className={'text-white text-[21px] sm:text-[55px] font-extrabold'}>{fallbackDisplayValue}</div>;
  } else {
    return <span className={'text-[21px] sm:text-[55px] font-extrabold'}>{displayTerm(value)}</span>;
  }
};

const ElementTileTargetIconTitle: ElementTitleFC = ({ financialElementName, className }) => {
  return (
    <div
      className={clsx(
        'text-[22px] font-medium leading-none text-wrap w-[44px] h-[42px] pt-1 grow data-[wordlength="2"]:[word-spacing:54px] data-[wordlength="3"]:[width:100px] tracking-tighter',
        className
      )}
      data-wordlength={financialElementName.split(' ').length}
    >
      {financialElementName}
    </div>
  );
};
type ElementTitleFC = FC<{ financialElementName: string; className?: string }>;

type ElementTileTargetIconProps = {
  financialElementAbbreviation: ScorecardElementSymbol;
  scoreValue?: number | null;
  scoreRender?: FC<{ value?: number | null }>;
  showScoreLine?: boolean;
  targetMin?: number | null;
  targetMax?: number | null;
  onClick?: () => void;
  beachMode?: boolean;
  className?: string;
};

type ElementsIconChipProps = {
  scoreValue?: number | null;
  targetMin?: number | null;
  targetMax?: number | null;
  financialElementAbbreviation: ScorecardElementSymbol;
  className?: string;
  formatTargetRangeValue?: (value?: number) => number;
  beachMode: boolean;
};

export type ElementTileEmoticonIndicatorIconName = 'happyFace' | 'sadFace' | 'neutralFace';

export const ElementTileIconTargetChip: FC<ElementsIconChipProps> = ({
  scoreValue,
  className,
  targetMin,
  targetMax,
  financialElementAbbreviation,
  beachMode,
}) => {
  const { viewOptions } = usePresentationViewOptionsContext();
  const { icon, displayRange } = useEmojiCalculation(
    financialElementAbbreviation,
    beachMode,
    scoreValue,
    targetMin,
    targetMax
  );

  return (viewOptions.showEmojis || viewOptions.showTargetScores) && scoreValue != null && targetMax != null ? (
    <div className={clsx('h-9 pl-2 pr-2.5 py-[7px] justify-center items-center gap-1 inline-flex relative', className)}>
      <div
        className={
          'absolute rounded-[18px] top-0 left-0 w-full h-full z-[1] bg-darkMode-gray-medium/40 mix-blend-multiply'
        }
      ></div>

      {viewOptions.showEmojis ? (
        <div className="w-6 h-6 relative z-[2]" data-icon-target-chip-range-emoji={icon}>
          {icon && <Icon icon={icon} />}
        </div>
      ) : (
        <div className="w-1/2" />
      )}

      {viewOptions.showTargetScores && (
        <div
          className="text-center text-white text-[22px] font-bold font-['Tenon'] leading-snug whitespace-nowrap z-[2]"
          data-testid={'iconTargetChipRangeValue'}
        >
          {displayRange || ''}
        </div>
      )}
    </div>
  ) : (
    <div className={'h-9'} />
  );
};

const ElementTileHeaderMobile: FC<ElementsIconChipProps> = ({
  scoreValue,
  targetMin,
  targetMax,
  financialElementAbbreviation,
  beachMode,
}) => {
  const { viewOptions } = usePresentationViewOptionsContext();
  const { icon } = useEmojiCalculation(financialElementAbbreviation, beachMode, scoreValue, targetMin, targetMax);

  return (
    <div className="w-full flex justify-between" data-icon-target-chip-range-emoji={icon}>
      <p className={'text-sm sm:text-3xl font-medium leading-4'}>{financialElementAbbreviation}</p>
      {viewOptions.showEmojis && icon && <Icon icon={icon} className={'w-4 h-4 sm:h-8 sm:w-8'} />}
    </div>
  );
};

export const ElementTileTargetIcon: FC<ElementTileTargetIconProps> = ({
  financialElementAbbreviation,
  scoreValue,
  scoreRender,
  targetMin,
  targetMax,
  onClick = () => {},
  showScoreLine,
  beachMode = false,
  className,
}) => {
  const { t } = useTranslation('scorecard');
  const financialElementName = displayScorecardElementName(financialElementAbbreviation, t);
  const isTermElement = isScorecardTermElement(financialElementAbbreviation);
  const estateStatusScoreIsMissing =
    financialElementAbbreviation === ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL && scoreValue == null;
  const { isMobileWidth } = usePresentationViewOptionsContext();

  return (
    <div
      className={clsx(
        className,
        'flex flex-col bg-palette-600 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] h-full rounded-lg sm:rounded-[21.6px] text-white px-2 pt-1 sm:pt-2 pb-0 sm:pl-[22px] gap-0 justify-between select-none cursor-pointer'
      )}
      onClick={() => onClick()}
      style={
        beachMode && isTermElement
          ? {
              backgroundImage: `url(${BeachModeIllustration})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'bottom',
            }
          : {}
      }
    >
      <div className={'flex flex-col flex-nowrap justify-between items-center gap-0 w-full h-full'}>
        {isMobileWidth ? (
          <ElementTileHeaderMobile
            scoreValue={scoreValue}
            className={'self-end'}
            targetMin={targetMin}
            targetMax={targetMax}
            financialElementAbbreviation={financialElementAbbreviation}
            beachMode={beachMode}
          />
        ) : showScoreLine ? (
          <ElementTileIconTargetChip
            scoreValue={scoreValue}
            className={'self-end'}
            targetMin={targetMin}
            targetMax={targetMax}
            financialElementAbbreviation={financialElementAbbreviation}
            beachMode={beachMode}
          />
        ) : (
          <div className={'h-4 sm:h-9'} />
        )}

        {!isMobileWidth && (
          <div className={'self-start flex flex-row justify-start flex-nowrap align-middle gap-0'}>
            <ElementTileTargetIconTitle
              financialElementName={financialElementName}
              className={elementsTitleTextTailwindClasses[financialElementAbbreviation]}
            />
          </div>
        )}
        <div className={'self-start lg:h-[72px]'}>
          {scoreRender ? (
            scoreRender({ value: scoreValue })
          ) : estateStatusScoreIsMissing ? (
            <ElementTileTargetIconNumericScore value={scoreValue} fallbackDisplayValue="––" />
          ) : isTermElement ? (
            !beachMode ? (
              <ElementTileTargetIconNumericScore value={scoreValue} />
            ) : null
          ) : (
            <ElementTileTargetIconPercentageScore percentage={scoreValue} />
          )}
        </div>
      </div>
    </div>
  );
};
